import { findNewestAssistantMessageIndex } from './sessionSummaryMerge';

/**
 * What this tab has last synced a session to: the `updatedAt` its body was
 * fetched at, and whether the summary still called the session streaming at
 * that same moment.
 *
 * Recorded only where the body is actually fetched (`AppSecure.tsx`), so it
 * is `undefined` for a session this tab has not fetched in its own lifetime —
 * including every session right after a reload, since the map holding these
 * markers lives in a `useRef` and starts empty every time the page loads.
 * `undefined` here means "never followed", not "has a stale copy" — see
 * `localCopyStreaming` below for that other question.
 */
export interface HydrationMarker {
  updatedAt: number | undefined;
  streaming: boolean;
}

/**
 * Whether the three-second tick has to pull a conversation's message bodies.
 *
 * The tick exists to follow a conversation streaming somewhere else — another
 * tab, another device. It pulled the whole session, every message body, every
 * three seconds, for as long as that conversation ran, whether or not anything
 * had changed.
 *
 * Measured 2026-09-16 on a live turn: two requests every three seconds, forty a
 * minute, one of them carrying the entire session. The organization's gateway
 * allows sixty a minute in total, shared with `agents/status`, artifacts,
 * entitlements and the SSE tickets — so one passive viewer spent two thirds of
 * the budget doing nothing. That budget running out is the documented cause of
 * a CODESYS validation dying at 429 with the compiler never invoked.
 *
 * The session row's `updatedAt` is what makes this decidable: a worker
 * checkpoint moves it, and a long quiet stretch — a model thinking for four
 * minutes — does not. So the cost now follows how much is actually happening
 * instead of being constant. But `updatedAt` alone is not always enough:
 *
 * I-1 (review of 8305b621): `reconcileInterruptedGenerations`
 * (auth-core) marks a crashed generation's message failed and clears
 * `is_streaming` WITHOUT bumping `updated_at` — deliberately, unlike every
 * normal completion path (complete/fail/cancel), which does bump it. A tab
 * that had already fetched this session while it was streaming recorded that
 * same `updatedAt` in its marker, so comparing `updatedAt` alone read the
 * flip as "nothing changed" and never fetched the row that would have shown
 * the failure — and the same tick stopped the whole poll, since no session
 * this tab knew of locally still read as streaming. Recording whether the
 * summary was streaming *at* that `updatedAt`, not just the number itself,
 * is what lets this one flip still be seen as a change.
 *
 * TA10c: a reload mid-turn restores a cached copy that is still streaming,
 * and the marker for it is `undefined` — this tab has never fetched this
 * session in its own lifetime. But "no marker" only means that; it is true of
 * every already-finished conversation in the user's history just as much as
 * of the one the reload actually interrupted, and the poll below runs this
 * function for every session in the list the moment any one of them is
 * streaming. Cost regression (review of 03aba39e): treating "no marker" alone
 * as reason to fetch pulled the whole body of every never-seen, already-
 * settled conversation in one tick. `localCopyStreaming` is what tells the
 * two apart — not a second signal for the marker's fact, but the only
 * information available when there is no marker: what this tab's own
 * restored cache looks like, from `isLocalCopyStreaming`.
 */
export function needsBodyFetch(
  summary: { id: string; isStreaming?: boolean; updatedAt?: number },
  lastFetchedMarker: HydrationMarker | undefined,
  generatingHere: boolean,
  localCopyStreaming = false,
): boolean {
  // Its own SSE stream is ahead of anything the server has stored.
  if (generatingHere) return false;
  if (!summary.isStreaming) {
    // Never followed here: only a restored copy still showing a live turn
    // needs the one-off catch-up (TA10c) — every other never-seen,
    // already-settled conversation must not be fetched just for lacking one.
    if (!lastFetchedMarker) return localCopyStreaming;
    // Followed while it streamed, and has stopped since — with or without an
    // `updatedAt` bump (I-1: `reconcileInterruptedGenerations` clears
    // `is_streaming` without moving `updated_at` on a crash).
    if (lastFetchedMarker.streaming) return true;
    // Settled when this tab last fetched it: only a real change pulls it
    // again. Deliberately does not fail open on a missing timestamp here,
    // unlike the streaming branch below — a session already settled at the
    // last fetch would otherwise be re-fetched every tick for the rest of
    // the poll, with no "next real change" to ever stop waiting for.
    return Boolean(summary.updatedAt) && summary.updatedAt !== lastFetchedMarker.updatedAt;
  }
  // Nothing to compare against yet.
  if (!lastFetchedMarker) return true;
  // Fail open. A missing timestamp is "cannot tell", not "unchanged" — reading
  // it the other way freezes a remote viewer for the whole turn.
  if (!summary.updatedAt) return true;
  return summary.updatedAt !== lastFetchedMarker.updatedAt;
}

/**
 * Whether this tab's own cached copy of a session still reads as streaming —
 * the `localCopyStreaming` input `needsBodyFetch` needs for a session it has
 * never fetched in its own lifetime, where the marker has nothing to say.
 *
 * Read from the session's own flag, or from its newest assistant message
 * only — never from whether *any* message is streaming. A merge can leave an
 * older message's `isStreaming: true` standing (kept because the local copy
 * had more output than the remote's at that message, or because the client's
 * clock briefly ran ahead of the server's) long after the session's actual
 * newest message has settled. The newest-message search is shared with
 * `applySessionLivenessToTurn` (`sessionSummaryMerge.ts`), which asks the
 * same question about the same array.
 */
export function isLocalCopyStreaming(
  session: { isStreaming?: boolean; messages?: { role: string; isStreaming?: boolean }[] } | undefined,
): boolean {
  if (session?.isStreaming) return true;
  const messages = session?.messages || [];
  const index = findNewestAssistantMessageIndex(messages);
  return index >= 0 ? Boolean(messages[index].isStreaming) : false;
}
